import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ToastViewport, type Toast } from '../components/atoms';

interface ToastApi {
  push: (text: string, kind?: Toast['kind']) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [items, setItems] = useState<Toast[]>([]);

  const push = useCallback<ToastApi['push']>((text, kind = '') => {
    const id = Math.random().toString(36).slice(2, 9);
    setItems((prev) => [...prev, { id, text, kind }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const timers = items.map((t) => window.setTimeout(() => dismiss(t.id), 3800));
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [items, dismiss]);

  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

export function useToasts(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToasts must be used inside <ToastProvider>');
  return ctx;
}
